import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-600 text-sm sm:text-base leading-relaxed">{children}</div>
  </section>
);

const LegalLayout = ({ children }) => (
  <div className="min-h-screen bg-slate-50" data-testid="legal-page">
    <header className="border-b border-slate-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-home-link">
          <img src="/logo.png" alt="OwlWorkLabs logo" className="w-9 h-9 object-contain" />
          <span className="text-lg font-bold text-slate-900">OwlWorkLabs</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">{children}</main>
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 py-8 text-center text-slate-500 text-xs">
        <p>© {new Date().getFullYear()} OwlWorkLabs Ltd. All rights reserved. · Company No. 16713122</p>
        <p className="mt-2">
          <Link to="/privacy" className="hover:text-slate-800 transition-colors">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-slate-800 transition-colors">Terms &amp; Conditions</Link>
        </p>
      </div>
    </footer>
  </div>
);

const Terms = () => {
  return (
    <LegalLayout>
      <SEO
        title="Terms & Conditions | OwlWorkLabs"
        description="The terms and conditions governing your use of the OwlWorkLabs website and its waitlist and information services."
        canonical="https://www.owlworklabs.com/terms"
      />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Legal</p>
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Terms &amp; Conditions</h1>
      <p className="text-sm text-slate-500 mt-3">Last updated: June 2026</p>

      <div className="mt-6 space-y-3 text-slate-600 text-sm sm:text-base leading-relaxed">
        <p>
          These Terms &amp; Conditions govern your use of the OwlWorkLabs website operated by OwlWorkLabs Ltd
          ("OwlWorkLabs", "we", "us"). By accessing or using this website, you agree to these terms. If you do
          not agree, please do not use the site.
        </p>
      </div>

      <Section title="Use of the website">
        <p>
          This website is provided for general information about OwlWorkLabs and its ventures. You agree to use
          it lawfully and not to misuse it, attempt to disrupt it, or access it in any way that could damage or
          impair its availability.
        </p>
      </Section>

      <Section title="Waitlist and pre-launch products">
        <p>
          Some products described on this site (including JADE and Collabity) are in development or pre-launch.
          Joining a waitlist does not constitute a purchase, contract, or guarantee of access, pricing, or
          availability. Features, timelines, and details may change.
        </p>
      </Section>

      <Section title="Intellectual property">
        <p>
          All content on this site — including text, logos, branding, and designs — is owned by or licensed to
          OwlWorkLabs Ltd and is protected by applicable intellectual property laws. You may not reproduce or
          reuse it without our permission.
        </p>
      </Section>

      <Section title="Third-party links">
        <p>
          Our site may link to third-party websites and services. We are not responsible for the content,
          policies, or practices of those third parties.
        </p>
      </Section>

      <Section title="Disclaimer and liability">
        <p>
          The website is provided on an "as is" basis. To the fullest extent permitted by law, we exclude
          warranties and are not liable for any loss arising from your use of, or inability to use, the site.
          Nothing in these terms limits liability that cannot be limited under law.
        </p>
      </Section>

      <Section title="Governing law">
        <p>
          These terms are governed by the laws of England and Wales, and any disputes are subject to the
          exclusive jurisdiction of the courts of England and Wales.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about these terms? Email us at{' '}
          <a href="mailto:owlworklabs@gmail.com" className="text-slate-900 underline">owlworklabs@gmail.com</a>.
        </p>
        {/* TODO: add UK company registration number and registered office address when available */}
      </Section>
    </LegalLayout>
  );
};

export default Terms;
